import { useCallback, useEffect, useRef, useState } from "react";
import { Sparkles, Send, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useApi, errMsg } from "@/lib/api";
import { useAiStream } from "@/lib/ai";
import { PageHeader, Panel, Loader } from "@/components/common";

const SUGGESTIONS = [
  "Hoeveel uur mag een 17-jarige per week werken?",
  "Hoe maak ik het rooster eerlijker voor iedereen?",
  "Wat doe ik als iemand niet op komt dagen?",
  "Tips voor een drukke vrijdagavond met weinig personeel",
];

const Bubble = ({ m }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ai-msg-${m.role}`}>
    <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap break-words ${m.role === "user" ? "bg-[#0D5C46] text-white rounded-br-md" : "bg-white border border-slate-200 text-slate-800 rounded-bl-md"}`}>
      {m.text || <span className="text-slate-400">Aan het nadenken…</span>}
    </div>
  </div>
);

export default function Assistant() {
  const api = useApi();
  const stream = useAiStream();
  const [msgs, setMsgs] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(() => api.get("/ai/chat").then((r) => setMsgs(r.data)), [api]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [msgs]);

  const send = async (q) => {
    const question = (q ?? text).trim();
    if (!question || busy) return;
    setText("");
    setBusy(true);
    setMsgs((p) => [...p, { role: "user", text: question }, { role: "assistant", text: "" }]);
    try {
      await stream("/ai/chat", { text: question }, (full) =>
        setMsgs((p) => [...p.slice(0, -1), { role: "assistant", text: full }])
      );
    } catch (e) {
      toast.error(e.message);
      setMsgs((p) => p.slice(0, -1));
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    if (!window.confirm("Hele gesprek wissen?")) return;
    try {
      await api.delete("/ai/chat");
      setMsgs([]);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="assistant-page">
      <PageHeader title="AI-assistent" subtitle="Stel vragen over planning, personeel en regels in de horeca.">
        {msgs?.length > 0 && <Button variant="outline" className="rounded-full" onClick={clear} data-testid="ai-clear-button"><Trash2 className="h-4 w-4 mr-2" />Gesprek wissen</Button>}
      </PageHeader>

      <Panel className="p-4 sm:p-5 bg-[#FAFAF7]">
        <div className="space-y-3 min-h-[240px] max-h-[55vh] overflow-y-auto pr-1" data-testid="ai-chat-list">
          {!msgs ? <Loader /> : msgs.length === 0 ? (
            <div className="py-6 text-center">
              <Sparkles className="mx-auto h-8 w-8 text-[#FF5A36]" />
              <p className="mt-2 font-semibold">Waar kan ik mee helpen?</p>
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {SUGGESTIONS.map((s, i) => (
                  <button key={s} onClick={() => send(s)} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-[#0D5C46] transition-colors" data-testid={`ai-suggestion-${i}`}>{s}</button>
                ))}
              </div>
            </div>
          ) : msgs.map((m, i) => <Bubble key={m.id || i} m={m} />)}
          <div ref={endRef} />
        </div>
        <div className="mt-4 flex gap-2 items-end">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Typ je vraag…" rows={2} className="resize-none bg-white" data-testid="ai-chat-input" />
          <Button onClick={() => send()} disabled={busy || !text.trim()} className="h-11 rounded-xl" data-testid="ai-chat-send-button" aria-label="Versturen"><Send className="h-4 w-4" /></Button>
        </div>
      </Panel>
      <p className="mt-3 flex items-start gap-2 text-xs text-slate-500" data-testid="ai-privacy-note">
        <ShieldCheck className="h-4 w-4 shrink-0 text-[#0D5C46]" />
        De AI (ChatGPT) krijgt alleen namen, rollen en contracturen van je team, geen e-mail, telefoon of uurloon. Controleer antwoorden over wetgeving altijd zelf.
      </p>
    </div>
  );
}
